import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { LoadingPage } from "@/components/shared/LoadingPage";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEnterprise } from "@/contexts/EnterpriseContext";
import {
  fetchMyEnterprise,
  updateMyEnterprise,
  type EnterpriseFullView,
  type UpdateEnterprisePayload,
} from "@/services/enterpriseContextService";

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormValues {
  name: string;
  logoUrl: string;
  logoUrlDark: string;
  primaryColor: string;
}

interface FormErrors {
  name?: string;
}

function formFromEnterprise(e: EnterpriseFullView): FormValues {
  return {
    name: e.name,
    logoUrl: e.logoUrl ?? "",
    logoUrlDark: e.logoUrlDark ?? "",
    primaryColor: e.primaryColor ?? "#000000",
  };
}

function validateForm(
  values: FormValues,
  t: (k: string) => string,
): FormErrors {
  const errors: FormErrors = {};
  if (!values.name.trim()) {
    errors.name = t("platformEnterprises.validationRequired");
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function EnterpriseSettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const { subdomain } = useEnterprise();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormValues | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  const QUERY_KEY = ["my-enterprise", subdomain] as const;

  const detailQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => fetchMyEnterprise(subdomain),
  });

  // Seed the form once data arrives
  useEffect(() => {
    if (detailQuery.data && form === null) {
      setForm(formFromEnterprise(detailQuery.data));
    }
  }, [detailQuery.data, form]);

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateEnterprisePayload) =>
      updateMyEnterprise(subdomain, payload),
    onSuccess: (updated) => {
      // Update both the local detail cache and the branding query
      queryClient.setQueryData(QUERY_KEY, updated);
      void queryClient.invalidateQueries({
        queryKey: ["enterprise-by-subdomain", subdomain],
      });
      void queryClient.invalidateQueries({ queryKey: ["my-enterprise"] });
      toast.success(t("enterpriseStaff.settings.saved"));
    },
    onError: () => {
      toast.error(t("platformEnterprises.saveError"));
    },
  });

  const handleChange = useCallback(
    (field: keyof FormValues) =>
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        setForm((prev) => (prev ? { ...prev, [field]: e.target.value } : prev));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      },
    [],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent): void => {
      e.preventDefault();
      if (!form) return;
      const errs = validateForm(form, t);
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }
      updateMutation.mutate({
        name: form.name.trim(),
        logoUrl: form.logoUrl.trim() || null,
        logoUrlDark: form.logoUrlDark.trim() || null,
        primaryColor: form.primaryColor.trim() || null,
      });
    },
    [form, t, updateMutation],
  );

  if (detailQuery.isLoading || form === null) return <LoadingPage />;

  return (
    <div className="p-4 md:p-8">
      <h1 className="mb-4 text-xl font-semibold text-foreground">
        {t("enterpriseStaff.settings.title")}
      </h1>

      <Card className="max-w-lg shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">
            {t("enterpriseStaff.settings.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="es-name">
                {t("enterpriseStaff.settings.name")}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                id="es-name"
                value={form.name}
                onChange={handleChange("name")}
                disabled={updateMutation.isPending}
              />
              {errors.name && (
                <p className="text-xs text-destructive">{errors.name}</p>
              )}
            </div>

            {/* Logo URL */}
            <div className="space-y-1.5">
              <Label htmlFor="es-logo">
                {t("enterpriseStaff.settings.logoUrl")}
              </Label>
              <Input
                id="es-logo"
                type="url"
                value={form.logoUrl}
                onChange={handleChange("logoUrl")}
                disabled={updateMutation.isPending}
                placeholder="https://…"
              />
            </div>

            {/* Dark Logo URL */}
            <div className="space-y-1.5">
              <Label htmlFor="es-logo-dark">
                {t("enterpriseStaff.settings.logoUrlDark")}
              </Label>
              <Input
                id="es-logo-dark"
                type="url"
                value={form.logoUrlDark}
                onChange={handleChange("logoUrlDark")}
                disabled={updateMutation.isPending}
                placeholder="https://…"
              />
              <p className="text-xs text-muted-foreground">
                {t("platformEnterprises.logoDarkHint")}
              </p>
            </div>

            {/* Primary color */}
            <div className="space-y-1.5">
              <Label htmlFor="es-color">
                {t("enterpriseStaff.settings.primaryColor")}
              </Label>
              <div className="flex items-center gap-3">
                <input
                  id="es-color"
                  type="color"
                  value={form.primaryColor}
                  onChange={handleChange("primaryColor")}
                  disabled={updateMutation.isPending}
                  className="h-9 w-12 cursor-pointer rounded border bg-background p-0.5"
                />
                <Input
                  value={form.primaryColor}
                  onChange={handleChange("primaryColor")}
                  disabled={updateMutation.isPending}
                  placeholder="#000000"
                  className="w-32 font-mono"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={updateMutation.isPending}
              className="mt-2"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                  {t("platformEnterprises.saving")}
                </>
              ) : (
                t("enterpriseStaff.settings.save")
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
